import React, { useState, useEffect } from 'react';
import { adminService } from '../services/authService';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  EyeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

interface User {
  id: string;
  email: string;
  name: string | null;
  isEmailVerified: boolean;
  isPremium: boolean;
  role: string;
  createdAt: string;
  lastLoginAt: string | null;
  _count: {
    dreams: number;
  };
}

interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    isPremium: '',
    isEmailVerified: '',
    dateRange: '',
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'activities' | 'statistics' | 'notifications' | 'backups' | 'audit-logs' | 'security' | 'preferences' | 'settings' | 'theme'>('details');
  const [userActivities, setUserActivities] = useState<any[]>([]);
  const [userStatistics, setUserStatistics] = useState<any>(null);
  const [userNotifications, setUserNotifications] = useState<any[]>([]);
  const [userBackups, setUserBackups] = useState<any[]>([]);
  const [userAuditLogs, setUserAuditLogs] = useState<any[]>([]);
  const [userSecuritySettings, setUserSecuritySettings] = useState<any>(null);
  const [userPreferences, setUserPreferences] = useState<any>(null);
  const [userSettings, setUserSettings] = useState<any>(null);
  const [userThemeSettings, setUserThemeSettings] = useState<any>(null);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [themeLoading, setThemeLoading] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    type: 'info',
    category: 'general',
    isImportant: false
  });
  const [backupForm, setBackupForm] = useState({
    backupType: 'full',
    description: ''
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
    currentPassword: ''
  });
  const [securityForm, setSecurityForm] = useState({
    twoFactorEnabled: false,
    loginNotifications: true,
    suspiciousActivityAlerts: true
  });
  const [preferencesForm, setPreferencesForm] = useState({
    language: 'tr',
    theme: 'dark',
    notifications: true,
    emailNotifications: true,
    pushNotifications: true,
    dreamReminders: true,
    weeklyReports: true,
    interpretationMethod: 'hybrid',
    privacyLevel: 'private',
    dataSharing: false
  });
  const [settingsForm, setSettingsForm] = useState({
    appVersion: '1.0.20',
    autoUpdate: true,
    betaFeatures: false,
    cacheEnabled: true,
    imageQuality: 'high',
    animationSpeed: 'normal',
    dataSync: true,
    dailyLimit: 10,
    reminderTime: '',
    weekendMode: false,
    offlineMode: false,
    debugMode: false,
    analyticsEnabled: true,
    crashReporting: true,
    telemetryEnabled: false,
    timezone: 'Europe/Istanbul',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24',
    currency: 'TRY'
  });
  const [themeForm, setThemeForm] = useState({
    primaryTheme: 'dark',
    accentColor: '#6366f1',
    secondaryColor: '#8b5cf6',
    backgroundColor: '#1f2937',
    surfaceColor: '#374151',
    textColor: '#f9fafb',
    borderColor: '#4b5563',
    fontSize: 'medium',
    fontFamily: 'system',
    lineHeight: 'normal',
    borderRadius: 'medium',
    spacing: 'normal',
    sidebarWidth: 'normal',
    headerHeight: 'normal',
    cardStyle: 'elevated',
    listStyle: 'card',
    animationEnabled: true,
    animationSpeed: 'normal',
    transitionDuration: 300,
    hoverEffects: true,
    loadingAnimations: true,
    customCSS: '',
    themePreset: 'default',
    mobileLayout: 'stacked',
    tabletLayout: 'adaptive',
    desktopLayout: 'sidebar',
    highContrast: false,
    reducedMotion: false,
    focusIndicators: true,
    colorBlindSupport: false
  });
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
    isPremium: false,
    isEmailVerified: false,
  });

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, searchTerm, filters, sortBy, sortOrder]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data: UsersResponse = await adminService.getUsers(
        pagination.page,
        pagination.limit,
        searchTerm,
        filters,
        sortBy,
        sortOrder
      );
      setUsers(data.users);
      setPagination(data.pagination);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (filterName: string, value: string) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      role: '',
      isPremium: '',
      isEmailVerified: '',
      dateRange: '',
    });
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const exportToCSV = () => {
    const csvHeaders = [
      'ID',
      'İsim',
      'Email',
      'Rol',
      'Premium',
      'Email Doğrulanmış',
      'Kayıt Tarihi',
      'Son Giriş',
      'Rüya Sayısı'
    ];

    const csvData = users.map(user => [
      user.id,
      user.name || 'İsimsiz',
      user.email,
      user.role,
      user.isPremium ? 'Evet' : 'Hayır',
      user.isEmailVerified ? 'Evet' : 'Hayır',
      formatDate(user.createdAt),
      formatDate(user.lastLoginAt),
      user._count.dreams
    ]);

    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `kullanicilar_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Hiç giriş yapmamış';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminService.createUser(formData);
      setShowCreateModal(false);
      setFormData({
        email: '',
        password: '',
        name: '',
        role: 'user',
        isPremium: false,
        isEmailVerified: false,
      });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      console.log('🔧 Updating user:', selectedUser.id, formData);
      const result = await adminService.updateUser(selectedUser.id, {
        email: formData.email,
        name: formData.name,
        role: formData.role,
        isPremium: Boolean(formData.isPremium),
        isEmailVerified: Boolean(formData.isEmailVerified),
      });
      console.log('✅ User updated:', result);
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
      alert('Kullanıcı başarıyla güncellendi');
    } catch (error) {
      console.error('❌ Error updating user:', error);
      alert('Kullanıcı güncellenirken hata oluştu: ' + (error as any)?.message);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await adminService.deleteUser(selectedUser.id);
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleViewDetails = async (user: User) => {
    try {
      const response = await adminService.getUserById(user.id);
      setUserDetails(response.user);
      setSelectedUser(user);
      setActiveTab('details');
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const handleTabChange = async (tab: 'details' | 'activities' | 'statistics' | 'notifications' | 'backups' | 'audit-logs' | 'security' | 'preferences' | 'settings' | 'theme') => {
    setActiveTab(tab);
    
    if (tab === 'activities' && selectedUser && userActivities.length === 0) {
      setActivitiesLoading(true);
      try {
        const response = await adminService.getUserActivities(selectedUser.id);
        setUserActivities(response.activities);
      } catch (error) {
        console.error('Error fetching user activities:', error);
      }
      setActivitiesLoading(false);
    }
    
    if (tab === 'statistics' && selectedUser && !userStatistics) {
      setStatisticsLoading(true);
      try {
        const response = await adminService.getUserStatistics(selectedUser.id);
        setUserStatistics(response.statistics);
      } catch (error) {
        console.error('Error fetching user statistics:', error);
      }
      setStatisticsLoading(false);
    }

    if (tab === 'notifications' && selectedUser && userNotifications.length === 0) {
      setNotificationsLoading(true);
      try {
        const response = await adminService.getUserNotifications(selectedUser.id);
        setUserNotifications(response.notifications);
      } catch (error) {
        console.error('Error fetching user notifications:', error);
      }
      setNotificationsLoading(false);
    }

    if (tab === 'backups' && selectedUser && userBackups.length === 0) {
      setBackupsLoading(true);
      try {
        const response = await adminService.getUserBackups(selectedUser.id);
        setUserBackups(response.backups);
      } catch (error) {
        console.error('Error fetching user backups:', error);
      }
      setBackupsLoading(false);
    }

    if (tab === 'audit-logs' && selectedUser && userAuditLogs.length === 0) {
      setAuditLogsLoading(true);
      try {
        const response = await adminService.getUserAuditLogs(selectedUser.id);
        setUserAuditLogs(response.auditLogs);
      } catch (error) {
        console.error('Error fetching user audit logs:', error);
      }
      setAuditLogsLoading(false);
    }

    if (tab === 'security' && selectedUser && !userSecuritySettings) {
      setSecurityLoading(true);
      try {
        const response = await adminService.getUserSecuritySettings(selectedUser.id);
        setUserSecuritySettings(response.securitySettings);
        setSecurityForm({
          twoFactorEnabled: response.securitySettings.twoFactorEnabled || false,
          loginNotifications: response.securitySettings.loginNotifications || true,
          suspiciousActivityAlerts: response.securitySettings.suspiciousActivityAlerts || true
        });
      } catch (error) {
        console.error('Error fetching user security settings:', error);
      }
      setSecurityLoading(false);
    }

    if (tab === 'preferences' && selectedUser && !userPreferences) {
      setPreferencesLoading(true);
      try {
        const response = await adminService.getUserPreferences(selectedUser.id);
        setUserPreferences(response.preferences);
        setPreferencesForm({
          language: response.preferences.language || 'tr',
          theme: response.preferences.theme || 'dark',
          notifications: response.preferences.notifications || true,
          emailNotifications: response.preferences.emailNotifications || true,
          pushNotifications: response.preferences.pushNotifications || true,
          dreamReminders: response.preferences.dreamReminders || true,
          weeklyReports: response.preferences.weeklyReports || true,
          interpretationMethod: response.preferences.interpretationMethod || 'hybrid',
          privacyLevel: response.preferences.privacyLevel || 'private',
          dataSharing: response.preferences.dataSharing || false
        });
      } catch (error) {
        console.error('Error fetching user preferences:', error);
      }
      setPreferencesLoading(false);
    }

  };

  const handleCreateNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await adminService.createUserNotification(selectedUser.id, notificationForm);
      setShowNotificationModal(false);
      setNotificationForm({
        title: '',
        message: '',
        type: 'info',
        category: 'general',
        isImportant: false
      });
      // Refresh notifications
      if (activeTab === 'notifications') {
        const response = await adminService.getUserNotifications(selectedUser.id);
        setUserNotifications(response.notifications);
      }
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const handleCreateBackup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await adminService.createUserBackup(selectedUser.id, backupForm);
      setShowBackupModal(false);
      setBackupForm({
        backupType: 'full',
        description: ''
      });
      // Refresh backups
      if (activeTab === 'backups') {
        const response = await adminService.getUserBackups(selectedUser.id);
        setUserBackups(response.backups);
      }
    } catch (error) {
      console.error('Error creating backup:', error);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Şifreler eşleşmiyor');
      return;
    }
    
    try {
      await adminService.changeUserPassword(selectedUser.id, {
        newPassword: passwordForm.newPassword,
        currentPassword: passwordForm.currentPassword
      });
      setShowPasswordModal(false);
      setPasswordForm({
        newPassword: '',
        confirmPassword: '',
        currentPassword: ''
      });
      alert('Şifre başarıyla değiştirildi');
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Şifre değiştirme hatası');
    }
  };

  const handleUpdateSecurity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await adminService.updateUserSecuritySettings(selectedUser.id, securityForm);
      setShowSecurityModal(false);
      // Refresh security settings
      if (activeTab === 'security') {
        const response = await adminService.getUserSecuritySettings(selectedUser.id);
        setUserSecuritySettings(response.securitySettings);
      }
      alert('Güvenlik ayarları güncellendi');
    } catch (error) {
      console.error('Error updating security settings:', error);
      alert('Güvenlik ayarları güncelleme hatası');
    }
  };

  const handleUpdatePreferences = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await adminService.updateUserPreferences(selectedUser.id, preferencesForm);
      setShowPreferencesModal(false);
      // Refresh preferences
      if (activeTab === 'preferences') {
        const response = await adminService.getUserPreferences(selectedUser.id);
        setUserPreferences(response.preferences);
      }
      alert('Kullanıcı tercihleri güncellendi');
    } catch (error) {
      console.error('Error updating preferences:', error);
      alert('Kullanıcı tercihleri güncelleme hatası');
    }
  };

  const handleResetPreferences = async () => {
    if (!selectedUser) return;
    
    if (window.confirm('Kullanıcı tercihlerini varsayılan değerlere sıfırlamak istediğinizden emin misiniz?')) {
      try {
        await adminService.resetUserPreferences(selectedUser.id);
        // Refresh preferences
        if (activeTab === 'preferences') {
          const response = await adminService.getUserPreferences(selectedUser.id);
          setUserPreferences(response.preferences);
          setPreferencesForm({
            language: response.preferences.language || 'tr',
            theme: response.preferences.theme || 'dark',
            notifications: response.preferences.notifications || true,
            emailNotifications: response.preferences.emailNotifications || true,
            pushNotifications: response.preferences.pushNotifications || true,
            dreamReminders: response.preferences.dreamReminders || true,
            weeklyReports: response.preferences.weeklyReports || true,
            interpretationMethod: response.preferences.interpretationMethod || 'hybrid',
            privacyLevel: response.preferences.privacyLevel || 'private',
            dataSharing: response.preferences.dataSharing || false
          });
        }
        alert('Kullanıcı tercihleri sıfırlandı');
      } catch (error) {
        console.error('Error resetting preferences:', error);
        alert('Kullanıcı tercihleri sıfırlama hatası');
      }
    }
  };

  const handleUpdateSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await adminService.updateUserSettings(selectedUser.id, settingsForm);
      setShowSettingsModal(false);
      // Refresh settings
      if (activeTab === 'settings') {
        const response = await adminService.getUserSettings(selectedUser.id);
        setUserSettings(response.settings);
      }
      alert('Kullanıcı ayarları güncellendi');
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Kullanıcı ayarları güncelleme hatası');
    }
  };

  const handleResetSettings = async () => {
    if (!selectedUser) return;
    
    if (window.confirm('Kullanıcı ayarlarını varsayılan değerlere sıfırlamak istediğinizden emin misiniz?')) {
      try {
        await adminService.resetUserSettings(selectedUser.id);
        // Refresh settings
        if (activeTab === 'settings') {
          const response = await adminService.getUserSettings(selectedUser.id);
          setUserSettings(response.settings);
          setSettingsForm({
            appVersion: response.settings.appVersion || '1.0.20',
            autoUpdate: response.settings.autoUpdate || true,
            betaFeatures: response.settings.betaFeatures || false,
            cacheEnabled: response.settings.cacheEnabled || true,
            imageQuality: response.settings.imageQuality || 'high',
            animationSpeed: response.settings.animationSpeed || 'normal',
            dataSync: response.settings.dataSync || true,
            dailyLimit: response.settings.dailyLimit || 10,
            reminderTime: response.settings.reminderTime || '',
            weekendMode: response.settings.weekendMode || false,
            offlineMode: response.settings.offlineMode || false,
            debugMode: response.settings.debugMode || false,
            analyticsEnabled: response.settings.analyticsEnabled || true,
            crashReporting: response.settings.crashReporting || true,
            telemetryEnabled: response.settings.telemetryEnabled || false,
            timezone: response.settings.timezone || 'Europe/Istanbul',
            dateFormat: response.settings.dateFormat || 'DD/MM/YYYY',
            timeFormat: response.settings.timeFormat || '24',
            currency: response.settings.currency || 'TRY'
          });
        }
        alert('Kullanıcı ayarları sıfırlandı');
      } catch (error) {
        console.error('Error resetting settings:', error);
        alert('Kullanıcı ayarları sıfırlama hatası');
      }
    }
  };

  const handleUpdateTheme = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await adminService.updateUserThemeSettings(selectedUser.id, themeForm);
      setShowThemeModal(false);
      // Refresh theme settings
      if (activeTab === 'theme') {
        const response = await adminService.getUserThemeSettings(selectedUser.id);
        setUserThemeSettings(response.themeSettings);
      }
      alert('Kullanıcı tema ayarları güncellendi');
    } catch (error) {
      console.error('Error updating theme:', error);
      alert('Kullanıcı tema ayarları güncelleme hatası');
    }
  };

  const handleResetTheme = async () => {
    if (!selectedUser) return;
    
    if (window.confirm('Kullanıcı tema ayarlarını varsayılan değerlere sıfırlamak istediğinizden emin misiniz?')) {
      try {
        await adminService.resetUserThemeSettings(selectedUser.id);
        // Refresh theme settings
        if (activeTab === 'theme') {
          const response = await adminService.getUserThemeSettings(selectedUser.id);
          setUserThemeSettings(response.themeSettings);
          setThemeForm({
            primaryTheme: response.themeSettings.primaryTheme || 'dark',
            accentColor: response.themeSettings.accentColor || '#6366f1',
            secondaryColor: response.themeSettings.secondaryColor || '#8b5cf6',
            backgroundColor: response.themeSettings.backgroundColor || '#1f2937',
            surfaceColor: response.themeSettings.surfaceColor || '#374151',
            textColor: response.themeSettings.textColor || '#f9fafb',
            borderColor: response.themeSettings.borderColor || '#4b5563',
            fontSize: response.themeSettings.fontSize || 'medium',
            fontFamily: response.themeSettings.fontFamily || 'system',
            lineHeight: response.themeSettings.lineHeight || 'normal',
            borderRadius: response.themeSettings.borderRadius || 'medium',
            spacing: response.themeSettings.spacing || 'normal',
            sidebarWidth: response.themeSettings.sidebarWidth || 'normal',
            headerHeight: response.themeSettings.headerHeight || 'normal',
            cardStyle: response.themeSettings.cardStyle || 'elevated',
            listStyle: response.themeSettings.listStyle || 'card',
            animationEnabled: response.themeSettings.animationEnabled || true,
            animationSpeed: response.themeSettings.animationSpeed || 'normal',
            transitionDuration: response.themeSettings.transitionDuration || 300,
            hoverEffects: response.themeSettings.hoverEffects || true,
            loadingAnimations: response.themeSettings.loadingAnimations || true,
            customCSS: response.themeSettings.customCSS || '',
            themePreset: response.themeSettings.themePreset || 'default',
            mobileLayout: response.themeSettings.mobileLayout || 'stacked',
            tabletLayout: response.themeSettings.tabletLayout || 'adaptive',
            desktopLayout: response.themeSettings.desktopLayout || 'sidebar',
            highContrast: response.themeSettings.highContrast || false,
            reducedMotion: response.themeSettings.reducedMotion || false,
            focusIndicators: response.themeSettings.focusIndicators || true,
            colorBlindSupport: response.themeSettings.colorBlindSupport || false
          });
        }
        alert('Kullanıcı tema ayarları sıfırlandı');
      } catch (error) {
        console.error('Error resetting theme:', error);
        alert('Kullanıcı tema ayarları sıfırlama hatası');
      }
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name || '',
      role: user.role,
      isPremium: user.isPremium,
      isEmailVerified: user.isEmailVerified,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  const handleBulkAction = async () => {
    if (selectedUsers.length === 0) return;

    try {
      if (bulkAction === 'delete') {
        for (const userId of selectedUsers) {
          await adminService.deleteUser(userId);
        }
      } else if (bulkAction === 'makePremium') {
        for (const userId of selectedUsers) {
          await adminService.updateUser(userId, { isPremium: true });
        }
      } else if (bulkAction === 'removePremium') {
        for (const userId of selectedUsers) {
          await adminService.updateUser(userId, { isPremium: false });
        }
      } else if (bulkAction === 'verifyEmail') {
        for (const userId of selectedUsers) {
          await adminService.updateUser(userId, { isEmailVerified: true });
        }
      }
      
      setShowBulkModal(false);
      setSelectedUsers([]);
      setBulkAction('');
      fetchUsers();
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Kullanıcılar</h1>
        <p className="mt-1 text-sm text-gray-500">
          Kayıtlı kullanıcıları görüntüleyin ve yönetin
        </p>
      </div>

      {/* Search and Actions */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center">
        <form onSubmit={handleSearch} className="w-full lg:max-w-md">
          <input
            type="text"
            placeholder="Kullanıcı ara..."
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </form>
        <div className="flex flex-wrap gap-2">
          {selectedUsers.length > 0 && (
            <button
              onClick={() => setShowBulkModal(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              Toplu İşlem ({selectedUsers.length})
            </button>
          )}
          <button
            onClick={exportToCSV}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">CSV İndir</span>
            <span className="sm:hidden">CSV</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-4 w-4 mr-1 lg:mr-2" />
            <span className="hidden sm:inline">Yeni Kullanıcı</span>
            <span className="sm:hidden">Yeni</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <option value="">Tüm Roller</option>
              <option value="user">Kullanıcı</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Süper Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Premium</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={filters.isPremium}
              onChange={(e) => handleFilterChange('isPremium', e.target.value)}
            >
              <option value="">Tümü</option>
              <option value="true">Premium</option>
              <option value="false">Normal</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Doğrulanmış</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={filters.isEmailVerified}
              onChange={(e) => handleFilterChange('isEmailVerified', e.target.value)}
            >
              <option value="">Tümü</option>
              <option value="true">Doğrulanmış</option>
              <option value="false">Doğrulanmamış</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sıralama</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="createdAt-desc">En Yeni</option>
              <option value="createdAt-asc">En Eski</option>
              <option value="name-asc">İsim A-Z</option>
              <option value="name-desc">İsim Z-A</option>
              <option value="email-asc">Email A-Z</option>
              <option value="email-desc">Email Z-A</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-between items-center">
          <button
            onClick={clearFilters}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Filtreleri Temizle
          </button>
          <div className="text-sm text-gray-500">
            Toplam {pagination.total} kullanıcı
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {/* Table Header */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedUsers.length === users.length && users.length > 0}
              onChange={handleSelectAll}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">
              Tümünü Seç ({selectedUsers.length}/{users.length})
            </span>
          </div>
        </div>
        <ul className="divide-y divide-gray-200">
          {users.map((user) => (
            <li key={user.id}>
              <div className="px-3 sm:px-4 py-4">
                {/* Mobile: Stack layout, Desktop: Flex row */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => handleSelectUser(user.id)}
                    className="h-4 w-4 mt-1 text-primary-600 focus:ring-primary-500 border-gray-300 rounded flex-shrink-0"
                    aria-label={`Select ${user.name || user.email}`}
                  />
                  
                  {/* Avatar */}
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-primary-500 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Main Content - Takes remaining space */}
                  <div className="flex-1 min-w-0">
                    {/* Name & Email */}
                    <div className="mb-2">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {user.name || 'İsimsiz Kullanıcı'}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {user.email}
                      </div>
                    </div>
                    
                    {/* Badges - Wrap on mobile */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {user.isEmailVerified && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Doğrulanmış
                        </span>
                      )}
                      {user.isPremium && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Premium
                        </span>
                      )}
                      {user.role === 'admin' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Admin
                        </span>
                      )}
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {user._count.dreams} rüya
                      </span>
                    </div>
                    
                    {/* Dates - Hidden on mobile, shown on larger screens */}
                    <div className="hidden sm:block text-xs text-gray-500">
                      <span>Kayıt: {formatDate(user.createdAt)}</span>
                      <span className="mx-2">•</span>
                      <span>Son: {formatDate(user.lastLoginAt)}</span>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mt-2 sm:mt-3">
                      <button
                        onClick={() => handleViewDetails(user)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <EyeIcon className="h-3.5 w-3.5 mr-1" />
                        Detay
                      </button>
                      <button
                        onClick={() => openEditModal(user)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <PencilIcon className="h-3.5 w-3.5 mr-1" />
                        Düzenle
                      </button>
                      {user.role !== 'admin' && user.role !== 'super_admin' && (
                        <button
                          onClick={() => openDeleteModal(user)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50"
                        >
                          <TrashIcon className="h-3.5 w-3.5 mr-1" />
                          Sil
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {users.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">Kullanıcı bulunamadı</p>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Önceki
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
              disabled={pagination.page === pagination.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Sonraki
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Toplam <span className="font-medium">{pagination.total}</span> kullanıcıdan{' '}
                <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> -{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span> arası gösteriliyor
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Önceki
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sonraki
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Yeni Kullanıcı Oluştur</h3>
              <form onSubmit={handleCreateUser}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Şifre</label>
                  <input
                    type="password"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">İsim</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="user">Kullanıcı</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Süper Admin</option>
                  </select>
                </div>
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    id="isPremium"
                    className="mr-2"
                    checked={formData.isPremium}
                    onChange={(e) => setFormData({ ...formData, isPremium: e.target.checked })}
                  />
                  <label htmlFor="isPremium" className="text-sm font-medium text-gray-700">Premium</label>
                </div>
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    id="isEmailVerified"
                    className="mr-2"
                    checked={formData.isEmailVerified}
                    onChange={(e) => setFormData({ ...formData, isEmailVerified: e.target.checked })}
                  />
                  <label htmlFor="isEmailVerified" className="text-sm font-medium text-gray-700">Email Doğrulanmış</label>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                  >
                    Oluştur
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Kullanıcı Düzenle</h3>
              <form onSubmit={handleEditUser}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">İsim</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="user">Kullanıcı</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Süper Admin</option>
                  </select>
                </div>
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    id="editIsPremium"
                    className="mr-2"
                    checked={formData.isPremium}
                    onChange={(e) => setFormData({ ...formData, isPremium: e.target.checked })}
                  />
                  <label htmlFor="editIsPremium" className="text-sm font-medium text-gray-700">Premium</label>
                </div>
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    id="editIsEmailVerified"
                    className="mr-2"
                    checked={formData.isEmailVerified}
                    onChange={(e) => setFormData({ ...formData, isEmailVerified: e.target.checked })}
                  />
                  <label htmlFor="editIsEmailVerified" className="text-sm font-medium text-gray-700">Email Doğrulanmış</label>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                  >
                    Güncelle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Kullanıcı Sil</h3>
              <p className="text-sm text-gray-600 mb-4">
                <strong>{selectedUser.name || 'İsimsiz Kullanıcı'}</strong> ({selectedUser.email}) kullanıcısını silmek istediğinizden emin misiniz?
                Bu işlem geri alınamaz.
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  İptal
                </button>
                <button
                  onClick={handleDeleteUser}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showDetailsModal && userDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Kullanıcı Detayları</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-4 overflow-x-auto">
                <button
                  onClick={() => handleTabChange('details')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'details'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Detaylar
                </button>
                <button
                  onClick={() => handleTabChange('activities')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'activities'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Aktivite Logları
                </button>
                <button
                  onClick={() => handleTabChange('statistics')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'statistics'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  İstatistikler
                </button>
                <button
                  onClick={() => handleTabChange('notifications')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'notifications'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Bildirimler
                </button>
                <button
                  onClick={() => handleTabChange('backups')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'backups'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Yedekler
                </button>
                <button
                  onClick={() => handleTabChange('audit-logs')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'audit-logs'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Audit Logları
                </button>
                <button
                  onClick={() => handleTabChange('security')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'security'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Güvenlik
                </button>
                <button
                  onClick={() => handleTabChange('preferences')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'preferences'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Tercihler
                </button>
              </nav>
            </div>
            
            {/* Tab Content */}
            {activeTab === 'details' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* User Info */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">Kullanıcı Bilgileri</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-600">İsim:</span>
                      <span className="ml-2 text-sm text-gray-900">{userDetails.name || 'İsimsiz'}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Email:</span>
                      <span className="ml-2 text-sm text-gray-900">{userDetails.email}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Rol:</span>
                      <span className="ml-2 text-sm text-gray-900">{userDetails.role}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Premium:</span>
                        <span className={`ml-2 text-sm font-bold ${userDetails.subscriptionStatus === 'PREMIUM' ? 'text-green-600' : 'text-gray-500'}`}>
                          {userDetails.subscriptionStatus === 'PREMIUM' ? `Aktif (${userDetails.subscriptionEndDate ? new Date(userDetails.subscriptionEndDate).toLocaleDateString('tr-TR') : 'Süresiz'})` : 'Hayır'}
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        {userDetails.subscriptionStatus !== 'PREMIUM' ? (
                          <>
                            <button
                              onClick={async () => {
                                try {
                                  await adminService.updateSubscription(userDetails.id, 'PREMIUM', 1);
                                  handleViewDetails(userDetails);
                                  alert('1 Ay Premium verildi');
                                } catch (e) { alert('Hata!'); }
                              }}
                              className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
                            >
                              1 Ay
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await adminService.updateSubscription(userDetails.id, 'PREMIUM', 12);
                                  handleViewDetails(userDetails);
                                  alert('1 Yıl Premium verildi');
                                } catch (e) { alert('Hata!'); }
                              }}
                              className="px-2 py-1 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700"
                            >
                              1 Yıl
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={async () => {
                              try {
                                await adminService.updateSubscription(userDetails.id, 'FREE');
                                handleViewDetails(userDetails);
                                alert('Premium kaldırıldı');
                              } catch (e) { alert('Hata!'); }
                            }}
                            className="px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700"
                          >
                            Premium Kaldır
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Horary Kredisi:</span>
                        <span className="ml-2 text-sm font-bold text-purple-600">{userDetails.horaryCredits || 0}</span>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={async () => {
                            try {
                              await adminService.addCredits(userDetails.id, 1, 'Admin +1');
                              handleViewDetails(userDetails);
                              alert('1 kredi eklendi');
                            } catch (e) { alert('Hata!'); }
                          }}
                          className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
                        >
                          +1
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await adminService.addCredits(userDetails.id, 5, 'Admin +5');
                              handleViewDetails(userDetails);
                              alert('5 kredi eklendi');
                            } catch (e) { alert('Hata!'); }
                          }}
                          className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
                        >
                          +5
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await adminService.addCredits(userDetails.id, 10, 'Admin +10');
                              handleViewDetails(userDetails);
                              alert('10 kredi eklendi');
                            } catch (e) { alert('Hata!'); }
                          }}
                          className="px-2 py-1 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700"
                        >
                          +10
                        </button>
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Email Doğrulanmış:</span>
                      <span className="ml-2 text-sm text-gray-900">{userDetails.isEmailVerified ? 'Evet' : 'Hayır'}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Kayıt Tarihi:</span>
                      <span className="ml-2 text-sm text-gray-900">{formatDate(userDetails.createdAt)}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Son Giriş:</span>
                      <span className="ml-2 text-sm text-gray-900">{formatDate(userDetails.lastLoginAt)}</span>
                    </div>
                  </div>
                </div>

                {/* Statistics */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">İstatistikler</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-600">Toplam Rüya:</span>
                      <span className="ml-2 text-sm text-gray-900">{userDetails._count?.dreams || 0}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Toplam Yorum:</span>
                      <span className="ml-2 text-sm text-gray-900">{userDetails._count?.interpretations || 0}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">Toplam Başarı:</span>
                      <span className="ml-2 text-sm text-gray-900">{userDetails._count?.achievements || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'activities' && (
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-3">Aktivite Logları</h4>
                {activitiesLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                ) : userActivities.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {userActivities.map((activity: any) => (
                      <div key={activity.id} className="p-3 bg-gray-50 rounded-md">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h6 className="text-sm font-medium text-gray-900">{activity.action}</h6>
                            {activity.description && (
                              <p className="text-xs text-gray-600 mt-1">{activity.description}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">{formatDate(activity.createdAt)}</p>
                          </div>
                          <div className="ml-2">
                            {activity.ipAddress && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {activity.ipAddress}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Henüz aktivite logu bulunmuyor</p>
                )}
              </div>
            )}

            {activeTab === 'statistics' && (
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-3">İstatistikler</h4>
                {statisticsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                ) : userStatistics ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-2">Genel İstatistikler</h5>
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm font-medium text-gray-600">Toplam Rüya:</span>
                          <span className="ml-2 text-sm text-gray-900">{userStatistics.totalDreams}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Toplam Yorum:</span>
                          <span className="ml-2 text-sm text-gray-900">{userStatistics.totalInterpretations}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600">Toplam Aktivite:</span>
                          <span className="ml-2 text-sm text-gray-900">{userStatistics.totalActivities}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-2">Aktivite Dağılımı</h5>
                      <div className="space-y-2">
                        {userStatistics.activityStats && userStatistics.activityStats.length > 0 ? (
                          userStatistics.activityStats.map((stat: any, index: number) => (
                            <div key={index} className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">{stat.action}</span>
                              <span className="text-sm font-medium text-gray-900">{stat._count}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500">Aktivite verisi bulunmuyor</p>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">İstatistik verisi yükleniyor...</p>
                )}
              </div>
            )}

            {activeTab === 'notifications' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Bildirimler</h4>
                  <button
                    onClick={() => setShowNotificationModal(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Yeni Bildirim
                  </button>
                </div>
                {notificationsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                ) : userNotifications.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {userNotifications.map((notification: any) => (
                      <div key={notification.id} className={`p-3 rounded-md ${
                        notification.isRead ? 'bg-gray-50' : 'bg-blue-50'
                      }`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h6 className="text-sm font-medium text-gray-900">{notification.title}</h6>
                              {notification.isImportant && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Önemli
                                </span>
                              )}
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                notification.type === 'error' ? 'bg-red-100 text-red-800' :
                                notification.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                notification.type === 'success' ? 'bg-green-100 text-green-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {notification.type}
                              </span>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                            <p className="text-xs text-gray-500 mt-1">{formatDate(notification.createdAt)}</p>
                          </div>
                          <div className="ml-2">
                            {!notification.isRead && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Yeni
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Henüz bildirim bulunmuyor</p>
                )}
              </div>
            )}

            {activeTab === 'backups' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Yedekler</h4>
                  <button
                    onClick={() => setShowBackupModal(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Yeni Yedek
                  </button>
                </div>
                {backupsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                ) : userBackups.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {userBackups.map((backup: any) => (
                      <div key={backup.id} className="p-3 bg-gray-50 rounded-md">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h6 className="text-sm font-medium text-gray-900">
                              {backup.backupType === 'full' ? 'Tam Yedek' : 
                               backup.backupType === 'partial' ? 'Kısmi Yedek' : 'Artırımlı Yedek'}
                            </h6>
                            {backup.description && (
                              <p className="text-xs text-gray-600 mt-1">{backup.description}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              Boyut: {(backup.size / 1024).toFixed(2)} KB | {formatDate(backup.createdAt)}
                            </p>
                          </div>
                          <div className="ml-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              backup.status === 'completed' ? 'bg-green-100 text-green-800' :
                              backup.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                              backup.status === 'failed' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {backup.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Henüz yedek bulunmuyor</p>
                )}
              </div>
            )}

            {activeTab === 'audit-logs' && (
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-3">Audit Logları</h4>
                {auditLogsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                ) : userAuditLogs.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {userAuditLogs.map((log: any) => (
                      <div key={log.id} className="p-3 bg-gray-50 rounded-md">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h6 className="text-sm font-medium text-gray-900">{log.action}</h6>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {log.entityType}
                              </span>
                            </div>
                            {log.reason && (
                              <p className="text-xs text-gray-600 mt-1">Neden: {log.reason}</p>
                            )}
                            {log.changedFields && (
                              <p className="text-xs text-gray-600 mt-1">Değişen alanlar: {log.changedFields}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              {log.ipAddress && `IP: ${log.ipAddress} | `}
                              {formatDate(log.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Henüz audit logu bulunmuyor</p>
                )}
              </div>
            )}

            {activeTab === 'security' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Güvenlik Ayarları</h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setShowPasswordModal(true)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Şifre Değiştir
                    </button>
                    <button
                      onClick={() => setShowSecurityModal(true)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Ayarları Düzenle
                    </button>
                  </div>
                </div>
                {securityLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                ) : userSecuritySettings ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Güvenlik Durumu</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">İki Faktörlü Doğrulama</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSecuritySettings.twoFactorEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSecuritySettings.twoFactorEnabled ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Giriş Bildirimleri</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSecuritySettings.loginNotifications ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSecuritySettings.loginNotifications ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Şüpheli Aktivite Uyarıları</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSecuritySettings.suspiciousActivityAlerts ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSecuritySettings.suspiciousActivityAlerts ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Şifre Bilgileri</h5>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-600">Son Şifre Değişikliği:</span>
                          <span className="ml-2 text-sm text-gray-900">
                            {userSecuritySettings.passwordLastChanged 
                              ? formatDate(userSecuritySettings.passwordLastChanged)
                              : 'Bilinmiyor'
                            }
                          </span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Başarısız Giriş Denemeleri:</span>
                          <span className="ml-2 text-sm text-gray-900">
                            {userSecuritySettings.failedLoginAttempts || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Hesap Durumu:</span>
                          <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSecuritySettings.accountLockedUntil 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {userSecuritySettings.accountLockedUntil ? 'Kilitli' : 'Aktif'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Güvenlik ayarları yükleniyor...</p>
                )}
              </div>
            )}

            {activeTab === 'preferences' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Kullanıcı Tercihleri</h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setShowPreferencesModal(true)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={handleResetPreferences}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Sıfırla
                    </button>
                  </div>
                </div>
                {preferencesLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                ) : userPreferences ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Genel Ayarlar</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Dil</span>
                          <span className="text-sm text-gray-900">
                            {userPreferences.language === 'tr' ? 'Türkçe' : 
                             userPreferences.language === 'en' ? 'English' : 
                             userPreferences.language}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Tema</span>
                          <span className="text-sm text-gray-900">
                            {userPreferences.theme === 'dark' ? 'Koyu' : 
                             userPreferences.theme === 'light' ? 'Açık' : 
                             userPreferences.theme}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Yorumlama Yöntemi</span>
                          <span className="text-sm text-gray-900">
                            {userPreferences.interpretationMethod === 'hybrid' ? 'Hibrit' :
                             userPreferences.interpretationMethod === 'islamic' ? 'İslami' :
                             userPreferences.interpretationMethod === 'psychological' ? 'Psikolojik' :
                             userPreferences.interpretationMethod === 'analytical' ? 'Analitik' :
                             userPreferences.interpretationMethod}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Gizlilik Seviyesi</span>
                          <span className="text-sm text-gray-900">
                            {userPreferences.privacyLevel === 'private' ? 'Özel' :
                             userPreferences.privacyLevel === 'public' ? 'Herkese Açık' :
                             userPreferences.privacyLevel === 'friends' ? 'Arkadaşlar' :
                             userPreferences.privacyLevel}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Bildirim Ayarları</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Genel Bildirimler</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userPreferences.notifications ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userPreferences.notifications ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">E-posta Bildirimleri</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userPreferences.emailNotifications ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userPreferences.emailNotifications ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Push Bildirimleri</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userPreferences.pushNotifications ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userPreferences.pushNotifications ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Rüya Hatırlatıcıları</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userPreferences.dreamReminders ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userPreferences.dreamReminders ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Haftalık Raporlar</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userPreferences.weeklyReports ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userPreferences.weeklyReports ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Veri Paylaşımı</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userPreferences.dataSharing ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userPreferences.dataSharing ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Kullanıcı tercihleri yükleniyor...</p>
                )}
              </div>
            )}

            {activeTab === 'settings' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Kullanıcı Ayarları</h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setShowSettingsModal(true)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={handleResetSettings}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Sıfırla
                    </button>
                  </div>
                </div>
                {settingsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                ) : userSettings ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Uygulama Ayarları</h5>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-600">Uygulama Versiyonu:</span>
                          <span className="ml-2 text-sm text-gray-900">{userSettings.appVersion}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Otomatik Güncelleme</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSettings.autoUpdate ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSettings.autoUpdate ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Beta Özellikler</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSettings.betaFeatures ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSettings.betaFeatures ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Performans Ayarları</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Cache</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSettings.cacheEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSettings.cacheEnabled ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Görsel Kalitesi:</span>
                          <span className="ml-2 text-sm text-gray-900">
                            {userSettings.imageQuality === 'high' ? 'Yüksek' :
                             userSettings.imageQuality === 'medium' ? 'Orta' :
                             userSettings.imageQuality === 'low' ? 'Düşük' :
                             userSettings.imageQuality}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Animasyon Hızı:</span>
                          <span className="ml-2 text-sm text-gray-900">
                            {userSettings.animationSpeed === 'fast' ? 'Hızlı' :
                             userSettings.animationSpeed === 'normal' ? 'Normal' :
                             userSettings.animationSpeed === 'slow' ? 'Yavaş' :
                             userSettings.animationSpeed}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Veri Senkronizasyonu</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSettings.dataSync ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSettings.dataSync ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Kullanım Ayarları</h5>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-600">Günlük Limit:</span>
                          <span className="ml-2 text-sm text-gray-900">{userSettings.dailyLimit} rüya</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Hatırlatıcı Saati:</span>
                          <span className="ml-2 text-sm text-gray-900">
                            {userSettings.reminderTime || 'Ayarlanmamış'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Hafta Sonu Modu</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSettings.weekendMode ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSettings.weekendMode ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Çevrimdışı Mod</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSettings.offlineMode ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSettings.offlineMode ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Gelişmiş Ayarlar</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Debug Modu</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSettings.debugMode ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSettings.debugMode ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Analitik</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSettings.analyticsEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSettings.analyticsEnabled ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Hata Raporlama</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSettings.crashReporting ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSettings.crashReporting ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Telemetri</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userSettings.telemetryEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userSettings.telemetryEnabled ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Sistem Ayarları</h5>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-600">Zaman Dilimi:</span>
                          <span className="ml-2 text-sm text-gray-900">{userSettings.timezone}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Tarih Formatı:</span>
                          <span className="ml-2 text-sm text-gray-900">{userSettings.dateFormat}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Saat Formatı:</span>
                          <span className="ml-2 text-sm text-gray-900">
                            {userSettings.timeFormat === '24' ? '24 Saat' : '12 Saat'}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Para Birimi:</span>
                          <span className="ml-2 text-sm text-gray-900">{userSettings.currency}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Kullanıcı ayarları yükleniyor...</p>
                )}
              </div>
            )}

            {activeTab === 'theme' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Kullanıcı Tema Ayarları</h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setShowThemeModal(true)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={handleResetTheme}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Sıfırla
                    </button>
                  </div>
                </div>
                {themeLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                ) : userThemeSettings ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Tema Renkleri</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Ana Tema</span>
                          <span className="text-sm text-gray-900 capitalize">{userThemeSettings.primaryTheme}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Vurgu Rengi</span>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: userThemeSettings.accentColor }}
                            ></div>
                            <span className="text-sm text-gray-900">{userThemeSettings.accentColor}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">İkincil Renk</span>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: userThemeSettings.secondaryColor }}
                            ></div>
                            <span className="text-sm text-gray-900">{userThemeSettings.secondaryColor}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Arka Plan</span>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: userThemeSettings.backgroundColor }}
                            ></div>
                            <span className="text-sm text-gray-900">{userThemeSettings.backgroundColor}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Yüzey Rengi</span>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: userThemeSettings.surfaceColor }}
                            ></div>
                            <span className="text-sm text-gray-900">{userThemeSettings.surfaceColor}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Görünüm Ayarları</h5>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-600">Font Boyutu:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.fontSize}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Font Ailesi:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.fontFamily}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Satır Yüksekliği:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.lineHeight}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Köşe Yuvarlaklığı:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.borderRadius}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Boşluk:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.spacing}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Layout Ayarları</h5>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-600">Sidebar Genişliği:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.sidebarWidth}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Header Yüksekliği:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.headerHeight}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Kart Stili:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.cardStyle}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Liste Stili:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.listStyle}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Tema Preseti:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.themePreset}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Animasyon Ayarları</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Animasyonlar</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userThemeSettings.animationEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userThemeSettings.animationEnabled ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Animasyon Hızı:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.animationSpeed}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Geçiş Süresi:</span>
                          <span className="ml-2 text-sm text-gray-900">{userThemeSettings.transitionDuration}ms</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Hover Efektleri</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userThemeSettings.hoverEffects ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userThemeSettings.hoverEffects ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Yükleme Animasyonları</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userThemeSettings.loadingAnimations ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userThemeSettings.loadingAnimations ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Responsive Ayarları</h5>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-gray-600">Mobil Layout:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.mobileLayout}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Tablet Layout:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.tabletLayout}</span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Desktop Layout:</span>
                          <span className="ml-2 text-sm text-gray-900 capitalize">{userThemeSettings.desktopLayout}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h5 className="text-md font-medium text-gray-800 mb-3">Erişilebilirlik</h5>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Yüksek Kontrast</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userThemeSettings.highContrast ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userThemeSettings.highContrast ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Azaltılmış Hareket</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userThemeSettings.reducedMotion ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userThemeSettings.reducedMotion ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Odak Göstergeleri</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userThemeSettings.focusIndicators ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userThemeSettings.focusIndicators ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Renk Körü Desteği</span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            userThemeSettings.colorBlindSupport ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userThemeSettings.colorBlindSupport ? 'Aktif' : 'Pasif'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Kullanıcı tema ayarları yükleniyor...</p>
                )}
              </div>
            )}

            {/* Recent Dreams - Only show in details tab */}
            {activeTab === 'details' && userDetails.dreams && userDetails.dreams.length > 0 && (
              <div className="mt-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3">Son Rüyalar</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {userDetails.dreams.slice(0, 5).map((dream: any) => (
                    <div key={dream.id} className="p-3 bg-gray-50 rounded-md">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h5 className="text-sm font-medium text-gray-900">{dream.title}</h5>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{dream.content}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(dream.createdAt)}</p>
                        </div>
                        <div className="ml-2">
                          {dream.interpretations && dream.interpretations.length > 0 ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Yorumlanmış
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Beklemede
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Achievements - Only show in details tab */}
            {activeTab === 'details' && userDetails.achievements && userDetails.achievements.length > 0 && (
              <div className="mt-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3">Başarılar</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {userDetails.achievements.map((achievement: any) => (
                    <div key={achievement.id} className={`p-2 rounded-md ${achievement.isUnlocked ? 'bg-green-50' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h6 className="text-sm font-medium text-gray-900">{achievement.title}</h6>
                          <p className="text-xs text-gray-600">{achievement.description}</p>
                        </div>
                        {achievement.isUnlocked && (
                          <span className="text-xs text-green-600">✓</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {showNotificationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Yeni Bildirim Oluştur</h3>
              <form onSubmit={handleCreateNotification}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Başlık</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={notificationForm.title}
                    onChange={(e) => setNotificationForm({ ...notificationForm, title: e.target.value })}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mesaj</label>
                  <textarea
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={notificationForm.message}
                    onChange={(e) => setNotificationForm({ ...notificationForm, message: e.target.value })}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tür</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={notificationForm.type}
                    onChange={(e) => setNotificationForm({ ...notificationForm, type: e.target.value })}
                  >
                    <option value="info">Bilgi</option>
                    <option value="success">Başarı</option>
                    <option value="warning">Uyarı</option>
                    <option value="error">Hata</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={notificationForm.category}
                    onChange={(e) => setNotificationForm({ ...notificationForm, category: e.target.value })}
                  >
                    <option value="general">Genel</option>
                    <option value="dream">Rüya</option>
                    <option value="achievement">Başarı</option>
                    <option value="system">Sistem</option>
                  </select>
                </div>
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    id="isImportant"
                    className="mr-2"
                    checked={notificationForm.isImportant}
                    onChange={(e) => setNotificationForm({ ...notificationForm, isImportant: e.target.checked })}
                  />
                  <label htmlFor="isImportant" className="text-sm font-medium text-gray-700">Önemli</label>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowNotificationModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                  >
                    Oluştur
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Backup Modal */}
      {showBackupModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Yeni Yedek Oluştur</h3>
              <form onSubmit={handleCreateBackup}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yedek Türü</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={backupForm.backupType}
                    onChange={(e) => setBackupForm({ ...backupForm, backupType: e.target.value })}
                  >
                    <option value="full">Tam Yedek</option>
                    <option value="partial">Kısmi Yedek</option>
                    <option value="incremental">Artırımlı Yedek</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={backupForm.description}
                    onChange={(e) => setBackupForm({ ...backupForm, description: e.target.value })}
                    placeholder="Yedek hakkında açıklama (opsiyonel)"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowBackupModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                  >
                    Oluştur
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Şifre Değiştir</h3>
              <form onSubmit={handleChangePassword}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mevcut Şifre (Opsiyonel)</label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    placeholder="Admin için opsiyonel"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre</label>
                  <input
                    type="password"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre (Tekrar)</label>
                  <input
                    type="password"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                  >
                    Şifre Değiştir
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Security Settings Modal */}
      {showSecurityModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Güvenlik Ayarları</h3>
              <form onSubmit={handleUpdateSecurity}>
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    id="twoFactorEnabled"
                    className="mr-2"
                    checked={securityForm.twoFactorEnabled}
                    onChange={(e) => setSecurityForm({ ...securityForm, twoFactorEnabled: e.target.checked })}
                  />
                  <label htmlFor="twoFactorEnabled" className="text-sm font-medium text-gray-700">İki Faktörlü Doğrulama</label>
                </div>
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    id="loginNotifications"
                    className="mr-2"
                    checked={securityForm.loginNotifications}
                    onChange={(e) => setSecurityForm({ ...securityForm, loginNotifications: e.target.checked })}
                  />
                  <label htmlFor="loginNotifications" className="text-sm font-medium text-gray-700">Giriş Bildirimleri</label>
                </div>
                <div className="mb-4 flex items-center">
                  <input
                    type="checkbox"
                    id="suspiciousActivityAlerts"
                    className="mr-2"
                    checked={securityForm.suspiciousActivityAlerts}
                    onChange={(e) => setSecurityForm({ ...securityForm, suspiciousActivityAlerts: e.target.checked })}
                  />
                  <label htmlFor="suspiciousActivityAlerts" className="text-sm font-medium text-gray-700">Şüpheli Aktivite Uyarıları</label>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowSecurityModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                  >
                    Güncelle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferencesModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Kullanıcı Tercihleri Düzenle</h3>
              <form onSubmit={handleUpdatePreferences}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dil</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={preferencesForm.language}
                      onChange={(e) => setPreferencesForm({ ...preferencesForm, language: e.target.value })}
                    >
                      <option value="tr">Türkçe</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tema</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={preferencesForm.theme}
                      onChange={(e) => setPreferencesForm({ ...preferencesForm, theme: e.target.value })}
                    >
                      <option value="dark">Koyu</option>
                      <option value="light">Açık</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Yorumlama Yöntemi</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={preferencesForm.interpretationMethod}
                      onChange={(e) => setPreferencesForm({ ...preferencesForm, interpretationMethod: e.target.value })}
                    >
                      <option value="hybrid">Hibrit</option>
                      <option value="islamic">İslami</option>
                      <option value="psychological">Psikolojik</option>
                      <option value="analytical">Analitik</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gizlilik Seviyesi</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={preferencesForm.privacyLevel}
                      onChange={(e) => setPreferencesForm({ ...preferencesForm, privacyLevel: e.target.value })}
                    >
                      <option value="private">Özel</option>
                      <option value="public">Herkese Açık</option>
                      <option value="friends">Arkadaşlar</option>
                    </select>
                  </div>
                </div>
                
                <div className="mt-4">
                  <h4 className="text-md font-medium text-gray-800 mb-3">Bildirim Ayarları</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="notifications"
                        className="mr-2"
                        checked={preferencesForm.notifications}
                        onChange={(e) => setPreferencesForm({ ...preferencesForm, notifications: e.target.checked })}
                      />
                      <label htmlFor="notifications" className="text-sm font-medium text-gray-700">Genel Bildirimler</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="emailNotifications"
                        className="mr-2"
                        checked={preferencesForm.emailNotifications}
                        onChange={(e) => setPreferencesForm({ ...preferencesForm, emailNotifications: e.target.checked })}
                      />
                      <label htmlFor="emailNotifications" className="text-sm font-medium text-gray-700">E-posta Bildirimleri</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="pushNotifications"
                        className="mr-2"
                        checked={preferencesForm.pushNotifications}
                        onChange={(e) => setPreferencesForm({ ...preferencesForm, pushNotifications: e.target.checked })}
                      />
                      <label htmlFor="pushNotifications" className="text-sm font-medium text-gray-700">Push Bildirimleri</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="dreamReminders"
                        className="mr-2"
                        checked={preferencesForm.dreamReminders}
                        onChange={(e) => setPreferencesForm({ ...preferencesForm, dreamReminders: e.target.checked })}
                      />
                      <label htmlFor="dreamReminders" className="text-sm font-medium text-gray-700">Rüya Hatırlatıcıları</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="weeklyReports"
                        className="mr-2"
                        checked={preferencesForm.weeklyReports}
                        onChange={(e) => setPreferencesForm({ ...preferencesForm, weeklyReports: e.target.checked })}
                      />
                      <label htmlFor="weeklyReports" className="text-sm font-medium text-gray-700">Haftalık Raporlar</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="dataSharing"
                        className="mr-2"
                        checked={preferencesForm.dataSharing}
                        onChange={(e) => setPreferencesForm({ ...preferencesForm, dataSharing: e.target.checked })}
                      />
                      <label htmlFor="dataSharing" className="text-sm font-medium text-gray-700">Veri Paylaşımı</label>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowPreferencesModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                  >
                    Güncelle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-5 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Kullanıcı Ayarları Düzenle</h3>
              <form onSubmit={handleUpdateSettings}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Uygulama Ayarları */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-800">Uygulama Ayarları</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Uygulama Versiyonu</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={settingsForm.appVersion}
                        onChange={(e) => setSettingsForm({ ...settingsForm, appVersion: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="autoUpdate"
                        className="mr-2"
                        checked={settingsForm.autoUpdate}
                        onChange={(e) => setSettingsForm({ ...settingsForm, autoUpdate: e.target.checked })}
                      />
                      <label htmlFor="autoUpdate" className="text-sm font-medium text-gray-700">Otomatik Güncelleme</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="betaFeatures"
                        className="mr-2"
                        checked={settingsForm.betaFeatures}
                        onChange={(e) => setSettingsForm({ ...settingsForm, betaFeatures: e.target.checked })}
                      />
                      <label htmlFor="betaFeatures" className="text-sm font-medium text-gray-700">Beta Özellikler</label>
                    </div>
                  </div>

                  {/* Performans Ayarları */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-800">Performans Ayarları</h4>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="cacheEnabled"
                        className="mr-2"
                        checked={settingsForm.cacheEnabled}
                        onChange={(e) => setSettingsForm({ ...settingsForm, cacheEnabled: e.target.checked })}
                      />
                      <label htmlFor="cacheEnabled" className="text-sm font-medium text-gray-700">Cache</label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Görsel Kalitesi</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={settingsForm.imageQuality}
                        onChange={(e) => setSettingsForm({ ...settingsForm, imageQuality: e.target.value })}
                      >
                        <option value="low">Düşük</option>
                        <option value="medium">Orta</option>
                        <option value="high">Yüksek</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Animasyon Hızı</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={settingsForm.animationSpeed}
                        onChange={(e) => setSettingsForm({ ...settingsForm, animationSpeed: e.target.value })}
                      >
                        <option value="slow">Yavaş</option>
                        <option value="normal">Normal</option>
                        <option value="fast">Hızlı</option>
                      </select>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="dataSync"
                        className="mr-2"
                        checked={settingsForm.dataSync}
                        onChange={(e) => setSettingsForm({ ...settingsForm, dataSync: e.target.checked })}
                      />
                      <label htmlFor="dataSync" className="text-sm font-medium text-gray-700">Veri Senkronizasyonu</label>
                    </div>
                  </div>

                  {/* Kullanım Ayarları */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-800">Kullanım Ayarları</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Günlük Limit</label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={settingsForm.dailyLimit}
                        onChange={(e) => setSettingsForm({ ...settingsForm, dailyLimit: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hatırlatıcı Saati (HH:MM)</label>
                      <input
                        type="time"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={settingsForm.reminderTime}
                        onChange={(e) => setSettingsForm({ ...settingsForm, reminderTime: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="weekendMode"
                        className="mr-2"
                        checked={settingsForm.weekendMode}
                        onChange={(e) => setSettingsForm({ ...settingsForm, weekendMode: e.target.checked })}
                      />
                      <label htmlFor="weekendMode" className="text-sm font-medium text-gray-700">Hafta Sonu Modu</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="offlineMode"
                        className="mr-2"
                        checked={settingsForm.offlineMode}
                        onChange={(e) => setSettingsForm({ ...settingsForm, offlineMode: e.target.checked })}
                      />
                      <label htmlFor="offlineMode" className="text-sm font-medium text-gray-700">Çevrimdışı Mod</label>
                    </div>
                  </div>

                  {/* Gelişmiş Ayarlar */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-800">Gelişmiş Ayarlar</h4>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="debugMode"
                        className="mr-2"
                        checked={settingsForm.debugMode}
                        onChange={(e) => setSettingsForm({ ...settingsForm, debugMode: e.target.checked })}
                      />
                      <label htmlFor="debugMode" className="text-sm font-medium text-gray-700">Debug Modu</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="analyticsEnabled"
                        className="mr-2"
                        checked={settingsForm.analyticsEnabled}
                        onChange={(e) => setSettingsForm({ ...settingsForm, analyticsEnabled: e.target.checked })}
                      />
                      <label htmlFor="analyticsEnabled" className="text-sm font-medium text-gray-700">Analitik</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="crashReporting"
                        className="mr-2"
                        checked={settingsForm.crashReporting}
                        onChange={(e) => setSettingsForm({ ...settingsForm, crashReporting: e.target.checked })}
                      />
                      <label htmlFor="crashReporting" className="text-sm font-medium text-gray-700">Hata Raporlama</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="telemetryEnabled"
                        className="mr-2"
                        checked={settingsForm.telemetryEnabled}
                        onChange={(e) => setSettingsForm({ ...settingsForm, telemetryEnabled: e.target.checked })}
                      />
                      <label htmlFor="telemetryEnabled" className="text-sm font-medium text-gray-700">Telemetri</label>
                    </div>
                  </div>

                  {/* Sistem Ayarları */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-800">Sistem Ayarları</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Zaman Dilimi</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={settingsForm.timezone}
                        onChange={(e) => setSettingsForm({ ...settingsForm, timezone: e.target.value })}
                      >
                        <option value="Europe/Istanbul">Europe/Istanbul</option>
                        <option value="Europe/London">Europe/London</option>
                        <option value="America/New_York">America/New_York</option>
                        <option value="Asia/Tokyo">Asia/Tokyo</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tarih Formatı</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={settingsForm.dateFormat}
                        onChange={(e) => setSettingsForm({ ...settingsForm, dateFormat: e.target.value })}
                      >
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Saat Formatı</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={settingsForm.timeFormat}
                        onChange={(e) => setSettingsForm({ ...settingsForm, timeFormat: e.target.value })}
                      >
                        <option value="24">24 Saat</option>
                        <option value="12">12 Saat</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Para Birimi</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={settingsForm.currency}
                        onChange={(e) => setSettingsForm({ ...settingsForm, currency: e.target.value })}
                      >
                        <option value="TRY">TRY</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowSettingsModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                  >
                    Güncelle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Theme Modal */}
      {showThemeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-5 mx-auto p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Kullanıcı Tema Ayarları Düzenle</h3>
              <form onSubmit={handleUpdateTheme}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Tema Renkleri */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-800">Tema Renkleri</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ana Tema</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.primaryTheme}
                        onChange={(e) => setThemeForm({ ...themeForm, primaryTheme: e.target.value })}
                      >
                        <option value="light">Açık</option>
                        <option value="dark">Koyu</option>
                        <option value="auto">Otomatik</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Vurgu Rengi</label>
                      <input
                        type="color"
                        className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.accentColor}
                        onChange={(e) => setThemeForm({ ...themeForm, accentColor: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">İkincil Renk</label>
                      <input
                        type="color"
                        className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.secondaryColor}
                        onChange={(e) => setThemeForm({ ...themeForm, secondaryColor: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Arka Plan Rengi</label>
                      <input
                        type="color"
                        className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.backgroundColor}
                        onChange={(e) => setThemeForm({ ...themeForm, backgroundColor: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Yüzey Rengi</label>
                      <input
                        type="color"
                        className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.surfaceColor}
                        onChange={(e) => setThemeForm({ ...themeForm, surfaceColor: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Metin Rengi</label>
                      <input
                        type="color"
                        className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.textColor}
                        onChange={(e) => setThemeForm({ ...themeForm, textColor: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Kenarlık Rengi</label>
                      <input
                        type="color"
                        className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.borderColor}
                        onChange={(e) => setThemeForm({ ...themeForm, borderColor: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Görünüm Ayarları */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-800">Görünüm Ayarları</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Font Boyutu</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.fontSize}
                        onChange={(e) => setThemeForm({ ...themeForm, fontSize: e.target.value })}
                      >
                        <option value="small">Küçük</option>
                        <option value="medium">Orta</option>
                        <option value="large">Büyük</option>
                        <option value="extra-large">Çok Büyük</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Font Ailesi</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.fontFamily}
                        onChange={(e) => setThemeForm({ ...themeForm, fontFamily: e.target.value })}
                      >
                        <option value="system">Sistem</option>
                        <option value="serif">Serif</option>
                        <option value="sans-serif">Sans-serif</option>
                        <option value="monospace">Monospace</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Satır Yüksekliği</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.lineHeight}
                        onChange={(e) => setThemeForm({ ...themeForm, lineHeight: e.target.value })}
                      >
                        <option value="tight">Sıkı</option>
                        <option value="normal">Normal</option>
                        <option value="relaxed">Rahat</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Köşe Yuvarlaklığı</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.borderRadius}
                        onChange={(e) => setThemeForm({ ...themeForm, borderRadius: e.target.value })}
                      >
                        <option value="none">Yok</option>
                        <option value="small">Küçük</option>
                        <option value="medium">Orta</option>
                        <option value="large">Büyük</option>
                        <option value="full">Tam</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Boşluk</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.spacing}
                        onChange={(e) => setThemeForm({ ...themeForm, spacing: e.target.value })}
                      >
                        <option value="compact">Kompakt</option>
                        <option value="normal">Normal</option>
                        <option value="comfortable">Rahat</option>
                      </select>
                    </div>
                  </div>

                  {/* Layout Ayarları */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-800">Layout Ayarları</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sidebar Genişliği</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.sidebarWidth}
                        onChange={(e) => setThemeForm({ ...themeForm, sidebarWidth: e.target.value })}
                      >
                        <option value="narrow">Dar</option>
                        <option value="normal">Normal</option>
                        <option value="wide">Geniş</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Header Yüksekliği</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.headerHeight}
                        onChange={(e) => setThemeForm({ ...themeForm, headerHeight: e.target.value })}
                      >
                        <option value="compact">Kompakt</option>
                        <option value="normal">Normal</option>
                        <option value="large">Büyük</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Kart Stili</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.cardStyle}
                        onChange={(e) => setThemeForm({ ...themeForm, cardStyle: e.target.value })}
                      >
                        <option value="flat">Düz</option>
                        <option value="elevated">Yükseltilmiş</option>
                        <option value="outlined">Çerçeveli</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Liste Stili</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.listStyle}
                        onChange={(e) => setThemeForm({ ...themeForm, listStyle: e.target.value })}
                      >
                        <option value="list">Liste</option>
                        <option value="card">Kart</option>
                        <option value="grid">Grid</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tema Preseti</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.themePreset}
                        onChange={(e) => setThemeForm({ ...themeForm, themePreset: e.target.value })}
                      >
                        <option value="default">Varsayılan</option>
                        <option value="minimal">Minimal</option>
                        <option value="colorful">Renkli</option>
                        <option value="professional">Profesyonel</option>
                      </select>
                    </div>
                  </div>

                  {/* Animasyon Ayarları */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-800">Animasyon Ayarları</h4>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="animationEnabled"
                        className="mr-2"
                        checked={themeForm.animationEnabled}
                        onChange={(e) => setThemeForm({ ...themeForm, animationEnabled: e.target.checked })}
                      />
                      <label htmlFor="animationEnabled" className="text-sm font-medium text-gray-700">Animasyonlar</label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Animasyon Hızı</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.animationSpeed}
                        onChange={(e) => setThemeForm({ ...themeForm, animationSpeed: e.target.value })}
                      >
                        <option value="slow">Yavaş</option>
                        <option value="normal">Normal</option>
                        <option value="fast">Hızlı</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Geçiş Süresi (ms)</label>
                      <input
                        type="number"
                        min="100"
                        max="1000"
                        step="50"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.transitionDuration}
                        onChange={(e) => setThemeForm({ ...themeForm, transitionDuration: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="hoverEffects"
                        className="mr-2"
                        checked={themeForm.hoverEffects}
                        onChange={(e) => setThemeForm({ ...themeForm, hoverEffects: e.target.checked })}
                      />
                      <label htmlFor="hoverEffects" className="text-sm font-medium text-gray-700">Hover Efektleri</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="loadingAnimations"
                        className="mr-2"
                        checked={themeForm.loadingAnimations}
                        onChange={(e) => setThemeForm({ ...themeForm, loadingAnimations: e.target.checked })}
                      />
                      <label htmlFor="loadingAnimations" className="text-sm font-medium text-gray-700">Yükleme Animasyonları</label>
                    </div>
                  </div>

                  {/* Responsive Ayarları */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-800">Responsive Ayarları</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Mobil Layout</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.mobileLayout}
                        onChange={(e) => setThemeForm({ ...themeForm, mobileLayout: e.target.value })}
                      >
                        <option value="stacked">Yığılmış</option>
                        <option value="tabs">Sekmeler</option>
                        <option value="drawer">Çekmece</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tablet Layout</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.tabletLayout}
                        onChange={(e) => setThemeForm({ ...themeForm, tabletLayout: e.target.value })}
                      >
                        <option value="adaptive">Uyarlanabilir</option>
                        <option value="desktop">Masaüstü</option>
                        <option value="mobile">Mobil</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Desktop Layout</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.desktopLayout}
                        onChange={(e) => setThemeForm({ ...themeForm, desktopLayout: e.target.value })}
                      >
                        <option value="sidebar">Sidebar</option>
                        <option value="topbar">Üst Çubuk</option>
                        <option value="minimal">Minimal</option>
                      </select>
                    </div>
                  </div>

                  {/* Erişilebilirlik Ayarları */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-800">Erişilebilirlik</h4>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="highContrast"
                        className="mr-2"
                        checked={themeForm.highContrast}
                        onChange={(e) => setThemeForm({ ...themeForm, highContrast: e.target.checked })}
                      />
                      <label htmlFor="highContrast" className="text-sm font-medium text-gray-700">Yüksek Kontrast</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="reducedMotion"
                        className="mr-2"
                        checked={themeForm.reducedMotion}
                        onChange={(e) => setThemeForm({ ...themeForm, reducedMotion: e.target.checked })}
                      />
                      <label htmlFor="reducedMotion" className="text-sm font-medium text-gray-700">Azaltılmış Hareket</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="focusIndicators"
                        className="mr-2"
                        checked={themeForm.focusIndicators}
                        onChange={(e) => setThemeForm({ ...themeForm, focusIndicators: e.target.checked })}
                      />
                      <label htmlFor="focusIndicators" className="text-sm font-medium text-gray-700">Odak Göstergeleri</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="colorBlindSupport"
                        className="mr-2"
                        checked={themeForm.colorBlindSupport}
                        onChange={(e) => setThemeForm({ ...themeForm, colorBlindSupport: e.target.checked })}
                      />
                      <label htmlFor="colorBlindSupport" className="text-sm font-medium text-gray-700">Renk Körü Desteği</label>
                    </div>
                  </div>

                  {/* Özel CSS */}
                  <div className="space-y-4 md:col-span-2 lg:col-span-3">
                    <h4 className="text-md font-medium text-gray-800">Özel CSS</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Özel CSS Kodu</label>
                      <textarea
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        value={themeForm.customCSS}
                        onChange={(e) => setThemeForm({ ...themeForm, customCSS: e.target.value })}
                        placeholder="/* Özel CSS kodunuzu buraya yazın */"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowThemeModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                  >
                    Güncelle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Toplu İşlem ({selectedUsers.length} kullanıcı)
              </h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">İşlem Seçin</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                >
                  <option value="">İşlem seçin...</option>
                  <option value="makePremium">Premium Yap</option>
                  <option value="removePremium">Premium Kaldır</option>
                  <option value="verifyEmail">Email Doğrula</option>
                  <option value="delete">Sil</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowBulkModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  İptal
                </button>
                <button
                  onClick={handleBulkAction}
                  disabled={!bulkAction}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Uygula
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;